import {
  AUDIT_ACTION,
  CHANGE_CATEGORY,
  CHANGE_STATUS,
  CHANGE_TYPE,
  CRITICALITY,
  LIFECYCLE_STEP,
  SCOPE_TYPE,
  TAuditAction,
  TAuditEvent,
  TChangeV2,
  URGENCY,
} from '../models';
import { createBlankChange } from '../utils/factory';
import { buildDemoCompletedPlan, buildDemoExecutionPlan } from './mockPlan';

// Fixed audit-event builder for deterministic seed data (no random ids / clocks).
let seedEventCounter = 0;
const ev = (timestamp: string, actor: string, action: TAuditAction, description: string): TAuditEvent => ({
  id: `seed-ev-${(seedEventCounter += 1)}`,
  timestamp,
  actor,
  action,
  description,
});

// Five realistic scenarios plus one partially completed draft, so the dashboard
// and representative states have enough to show. This is seed data for the
// prototype; a confideck developer can add more via the org mock config.
export const MOCK_CHANGES: TChangeV2[] = [
  // 1) Normal product change, accepted and in assessment.
  createBlankChange('Dr. Anna Keller', {
    id: 'chg-0007',
    displayId: 'CHG-2026-0007',
    title: 'Titanium supplier change for Cardionyx',
    status: CHANGE_STATUS.IN_ASSESSMENT,
    lifecycleStep: LIFECYCLE_STEP.ASSESSMENT,
    changeManager: 'Lukas Poterpin',
    createdAt: '2026-06-20T09:00:00.000Z',
    requestDate: '2026-06-20T00:00:00.000Z',
    targetDate: '2026-08-30T00:00:00.000Z',
    changeCategory: CHANGE_CATEGORY.MAINTENANCE,
    changeType: CHANGE_TYPE.PRODUCT,
    scopeType: SCOPE_TYPE.PRODUCT,
    criticality: CRITICALITY.MEDIUM,
    urgency: URGENCY.NORMAL,
    description: 'Replace the current titanium raw-material supplier following a discontinuation notice.',
    reasonOrTrigger: 'The current supplier discontinued the qualified titanium grade.',
    desiredOutcome: 'A qualified alternative supplier with equivalent material characteristics.',
    affectedProducts: ['Cardionyx'],
    affectedProductFamilies: ['Cardionyx'],
    currentOwner: 'Lukas Poterpin',
    progress: 35,
    nextAction: 'Complete supplier qualification assessment',
    assessorFunctions: [],
    tasks: [
      {
        id: 'chg-0007-t1',
        title: 'Assessment: Quality assurance',
        kind: 'assessment',
        assignedFunction: 'Quality assurance',
        status: 'In progress',
      },
      {
        id: 'chg-0007-t2',
        title: 'Assessment: Engineering or R&D',
        kind: 'assessment',
        assignedFunction: 'Engineering or R&D',
        status: 'Open',
      },
    ],
    auditEvents: [
      ev('2026-06-20T09:00:00.000Z', 'Dr. Anna Keller', AUDIT_ACTION.DRAFT_CREATED, 'Draft created.'),
      ev(
        '2026-06-21T10:15:00.000Z',
        'Dr. Anna Keller',
        AUDIT_ACTION.REQUEST_SUBMITTED,
        'Request submitted for assessment.'
      ),
      ev('2026-06-22T08:30:00.000Z', 'Lukas Poterpin', AUDIT_ACTION.TRIAGE_ACCEPTED, 'Accepted for assessment.'),
      ev('2026-06-22T08:30:05.000Z', 'System', AUDIT_ACTION.CHANGE_ID_GENERATED, 'Change ID CHG-2026-0007 generated.'),
    ],
    comments: [
      {
        id: 'chg-0007-c1',
        author: 'Lukas Poterpin',
        authorRole: 'Change manager',
        createdAt: '2026-06-22T09:00:00.000Z',
        body: 'Assigned to quality and engineering for assessment.',
      },
    ],
  }),

  // 2) QMS change, submitted and ready to begin assessment.
  createBlankChange('Julia Nowak', {
    id: 'chg-0008',
    displayId: 'CHG-2026-0008',
    title: 'Update document control procedure for electronic signatures',
    status: CHANGE_STATUS.SUBMITTED,
    lifecycleStep: LIFECYCLE_STEP.REQUEST,
    createdAt: '2026-07-08T09:00:00.000Z',
    requestDate: '2026-07-08T00:00:00.000Z',
    scopeType: SCOPE_TYPE.QMS,
    urgency: URGENCY.NORMAL,
    description: 'Revise the document control procedure to reflect the new electronic signature workflow.',
    reasonOrTrigger: 'The electronic signature workflow changed and the procedure is out of date.',
    affectedQmsAreas: ['Document control'],
    currentOwner: 'Lukas Poterpin',
    progress: 10,
    nextAction: 'Begin assessment',
    auditEvents: [
      ev('2026-07-08T09:00:00.000Z', 'Julia Nowak', AUDIT_ACTION.DRAFT_CREATED, 'Draft created.'),
      ev(
        '2026-07-09T14:20:00.000Z',
        'Julia Nowak',
        AUDIT_ACTION.REQUEST_SUBMITTED,
        'Request submitted for assessment.'
      ),
    ],
  }),

  // 3) CAPA-linked change, in assessment.
  createBlankChange('Dr. Sofia Meier', {
    id: 'chg-0006',
    displayId: 'CHG-2026-0006',
    title: 'Corrective change for recurring packaging seal deviation',
    status: CHANGE_STATUS.IN_ASSESSMENT,
    lifecycleStep: LIFECYCLE_STEP.ASSESSMENT,
    changeManager: 'Dr. Anna Keller',
    createdAt: '2026-06-10T09:00:00.000Z',
    requestDate: '2026-06-10T00:00:00.000Z',
    targetDate: '2026-08-10T00:00:00.000Z',
    changeCategory: CHANGE_CATEGORY.NONCONFORMITY,
    changeType: CHANGE_TYPE.PRODUCTION,
    scopeType: SCOPE_TYPE.PRODUCT,
    criticality: CRITICALITY.HIGH,
    urgency: URGENCY.HIGH,
    description: 'Address the recurring sterile-barrier seal deviation identified during production.',
    reasonOrTrigger: 'A recurring packaging seal deviation was confirmed and linked to a CAPA.',
    affectedProducts: ['Cardionyx'],
    affectedProductFamilies: ['Cardionyx'],
    sourceRecord: 's-capa-012',
    capaRequired: true,
    capaId: 'CAPA-2026-012',
    currentOwner: 'Dr. Anna Keller',
    progress: 45,
    nextAction: 'Complete production process assessment',
    tasks: [
      {
        id: 'chg-0006-t1',
        title: 'Assessment: Production or operations',
        kind: 'assessment',
        assignedFunction: 'Production or operations',
        status: 'In progress',
      },
      {
        id: 'chg-0006-t2',
        title: 'Assessment: Quality assurance',
        kind: 'assessment',
        assignedFunction: 'Quality assurance',
        status: 'Open',
      },
    ],
    auditEvents: [
      ev(
        '2026-06-10T09:00:00.000Z',
        'Dr. Sofia Meier',
        AUDIT_ACTION.DRAFT_CREATED,
        'Draft created from CAPA-2026-012.'
      ),
      ev(
        '2026-06-11T11:00:00.000Z',
        'Dr. Sofia Meier',
        AUDIT_ACTION.REQUEST_SUBMITTED,
        'Request submitted for assessment.'
      ),
      ev('2026-06-12T08:00:00.000Z', 'Dr. Anna Keller', AUDIT_ACTION.TRIAGE_ACCEPTED, 'Accepted for assessment.'),
    ],
  }),

  // 4) Blocked change, overdue.
  createBlankChange('Martin Vogt', {
    id: 'chg-0004',
    displayId: 'CHG-2026-0004',
    title: 'Alternative connector for CerebroTriage',
    status: CHANGE_STATUS.IN_ASSESSMENT,
    lifecycleStep: LIFECYCLE_STEP.ASSESSMENT,
    changeManager: 'Lukas Poterpin',
    createdAt: '2026-05-05T09:00:00.000Z',
    requestDate: '2026-05-05T00:00:00.000Z',
    targetDate: '2026-07-05T00:00:00.000Z',
    changeCategory: CHANGE_CATEGORY.MAINTENANCE,
    changeType: CHANGE_TYPE.PRODUCT,
    scopeType: SCOPE_TYPE.PRODUCT,
    criticality: CRITICALITY.HIGH,
    urgency: URGENCY.HIGH,
    description: 'Qualify an alternative connector after a component obsolescence notice.',
    reasonOrTrigger: 'The current connector is being discontinued by the supplier.',
    affectedProducts: ['CerebroTriage'],
    affectedProductFamilies: ['CerebroTriage'],
    currentOwner: 'Martin Vogt',
    progress: 40,
    blockers: ['Waiting on supplier first-article samples'],
    nextAction: 'Follow up with supplier on first-article samples',
    tasks: [
      {
        id: 'chg-0004-t1',
        title: 'Assessment: Engineering or R&D',
        kind: 'assessment',
        assignedFunction: 'Engineering or R&D',
        status: 'In progress',
      },
    ],
    auditEvents: [
      ev('2026-05-05T09:00:00.000Z', 'Martin Vogt', AUDIT_ACTION.DRAFT_CREATED, 'Draft created.'),
      ev(
        '2026-05-06T10:00:00.000Z',
        'Martin Vogt',
        AUDIT_ACTION.REQUEST_SUBMITTED,
        'Request submitted for assessment.'
      ),
      ev('2026-05-07T09:00:00.000Z', 'Lukas Poterpin', AUDIT_ACTION.TRIAGE_ACCEPTED, 'Accepted for assessment.'),
    ],
  }),

  // 5) Change awaiting an external notified-body decision.
  createBlankChange('Elena Rossi', {
    id: 'chg-0005',
    displayId: 'CHG-2026-0005',
    title: 'Intended-purpose wording update for Ivexis',
    status: CHANGE_STATUS.IN_ASSESSMENT,
    lifecycleStep: LIFECYCLE_STEP.ASSESSMENT,
    changeManager: 'Elena Rossi',
    createdAt: '2026-05-20T09:00:00.000Z',
    requestDate: '2026-05-20T00:00:00.000Z',
    targetDate: '2026-09-15T00:00:00.000Z',
    changeCategory: CHANGE_CATEGORY.MAINTENANCE,
    changeType: CHANGE_TYPE.MIXED,
    scopeType: SCOPE_TYPE.QMS_AND_TD,
    criticality: CRITICALITY.HIGH,
    urgency: URGENCY.NORMAL,
    description: 'Refine the intended-purpose wording following a clarification of the clinical indication.',
    reasonOrTrigger: 'A clarification of the clinical indication requires an intended-purpose update.',
    affectedProducts: ['Ivexis'],
    affectedProductFamilies: ['Ivexis'],
    awaitingExternalDecision: true,
    currentOwner: 'Elena Rossi',
    progress: 60,
    blockers: ['Awaiting notified body assessment of the significance evaluation'],
    nextAction: 'Awaiting notified body decision',
    tasks: [
      {
        id: 'chg-0005-t1',
        title: 'Assessment: Regulatory affairs',
        kind: 'assessment',
        assignedFunction: 'Regulatory affairs',
        status: 'Done',
      },
      {
        id: 'chg-0005-t2',
        title: 'Assessment: Clinical',
        kind: 'assessment',
        assignedFunction: 'Clinical',
        status: 'In progress',
      },
    ],
    auditEvents: [
      ev('2026-05-20T09:00:00.000Z', 'Elena Rossi', AUDIT_ACTION.DRAFT_CREATED, 'Draft created.'),
      ev(
        '2026-05-21T10:00:00.000Z',
        'Elena Rossi',
        AUDIT_ACTION.REQUEST_SUBMITTED,
        'Request submitted for assessment.'
      ),
      ev('2026-05-22T09:00:00.000Z', 'Elena Rossi', AUDIT_ACTION.TRIAGE_ACCEPTED, 'Accepted for assessment.'),
    ],
  }),

  // 6) Supplier/component change in execution - the fully populated Screen 8 demo.
  createBlankChange('Dr. Anna Keller', {
    id: 'chg-0044',
    displayId: 'CHG-2026-0044',
    title: 'Replace Sensor Patch component SP-100 with SP-200',
    status: CHANGE_STATUS.IN_EXECUTION,
    lifecycleStep: LIFECYCLE_STEP.EXECUTION,
    changeManager: 'Lukas Poterpin',
    createdAt: '2026-06-01T09:00:00.000Z',
    requestDate: '2026-06-01T00:00:00.000Z',
    targetDate: '2026-09-01T00:00:00.000Z',
    changeCategory: CHANGE_CATEGORY.MAINTENANCE,
    changeType: CHANGE_TYPE.PRODUCT,
    scopeType: SCOPE_TYPE.PRODUCT,
    criticality: CRITICALITY.HIGH,
    urgency: URGENCY.NORMAL,
    description:
      'Replace the discontinued Sensor Patch component SP-100 with SP-200 from a new supplier, including a revised patient-contacting adhesive.',
    reasonOrTrigger: 'The current supplier discontinued SP-100.',
    affectedProducts: ['Cardionyx'],
    affectedProductFamilies: ['Cardionyx'],
    awaitingExternalDecision: true,
    currentOwner: 'Lukas Poterpin',
    progress: 78,
    nextAction: 'Coordinate execution',
    plan: buildDemoExecutionPlan(),
    auditEvents: [
      ev('2026-06-01T09:00:00.000Z', 'Dr. Anna Keller', AUDIT_ACTION.DRAFT_CREATED, 'Draft created.'),
      ev(
        '2026-06-02T10:00:00.000Z',
        'Dr. Anna Keller',
        AUDIT_ACTION.REQUEST_SUBMITTED,
        'Request submitted for assessment.'
      ),
      ev('2026-06-03T09:00:00.000Z', 'Lukas Poterpin', AUDIT_ACTION.TRIAGE_ACCEPTED, 'Accepted for assessment.'),
      ev(
        '2026-07-07T09:00:00.000Z',
        'Lukas Poterpin',
        AUDIT_ACTION.PLAN_CREATED,
        'Implementation plan created from the approved assessment.'
      ),
      ev(
        '2026-07-08T09:00:00.000Z',
        'Lukas Poterpin',
        AUDIT_ACTION.PLAN_EXECUTION_STARTED,
        'Plan version 1 frozen and execution started.'
      ),
    ],
  }),

  // 7) Completion in progress. Effectiveness is shown only because this CAPA-linked Change requires it.
  createBlankChange('Dr. Sofia Meier', {
    id: 'chg-0043',
    displayId: 'CHG-2026-0043',
    title: 'Packaging seal process correction',
    status: CHANGE_STATUS.IN_COMPLETION,
    lifecycleStep: LIFECYCLE_STEP.COMPLETION,
    changeManager: 'Lukas Poterpin',
    createdAt: '2026-05-10T09:00:00.000Z',
    requestDate: '2026-05-10T00:00:00.000Z',
    targetDate: '2026-07-20T00:00:00.000Z',
    changeCategory: CHANGE_CATEGORY.NONCONFORMITY,
    changeType: CHANGE_TYPE.PRODUCTION,
    scopeType: SCOPE_TYPE.PRODUCT,
    criticality: CRITICALITY.HIGH,
    urgency: URGENCY.HIGH,
    description: 'Correct the sealing parameters and inspection controls for the sterile barrier process.',
    reasonOrTrigger: 'Recurring packaging seal deviations were confirmed through CAPA-2026-009.',
    affectedProducts: ['Cardionyx'],
    capaRequired: true,
    capaId: 'CAPA-2026-009',
    currentOwner: 'Lukas Poterpin',
    progress: 93,
    nextAction: 'Complete the effectiveness review',
    plan: buildDemoCompletedPlan(),
    completion: {
      effectivenessReviewRequired: true,
      releaseReadinessCheckedAt: '2026-07-14T09:00:00.000Z',
      releaseReadinessCheckedBy: 'Lukas Poterpin',
      releaseApprovedAt: '2026-07-15T09:00:00.000Z',
      releaseApprovedBy: 'Lukas Poterpin',
    },
    auditEvents: [
      ev('2026-07-14T09:00:00.000Z', 'Lukas Poterpin', AUDIT_ACTION.COMPLETION_STARTED, 'Completion started.'),
      ev(
        '2026-07-14T09:05:00.000Z',
        'Lukas Poterpin',
        AUDIT_ACTION.RELEASE_READINESS_CHECKED,
        'Release readiness checked.'
      ),
      ev('2026-07-15T09:00:00.000Z', 'Lukas Poterpin', AUDIT_ACTION.RELEASE_APPROVED, 'Release approved.'),
    ],
  }),

  // 8) Closed is a status on the Completion phase, not another lifecycle step.
  createBlankChange('Julia Nowak', {
    id: 'chg-0042',
    displayId: 'CHG-2026-0042',
    title: 'Electronic signature procedure update',
    status: CHANGE_STATUS.CLOSED,
    lifecycleStep: LIFECYCLE_STEP.COMPLETION,
    changeManager: 'Lukas Poterpin',
    createdAt: '2026-04-01T09:00:00.000Z',
    requestDate: '2026-04-01T00:00:00.000Z',
    targetDate: '2026-06-30T00:00:00.000Z',
    changeCategory: CHANGE_CATEGORY.MAINTENANCE,
    changeType: CHANGE_TYPE.QMS,
    scopeType: SCOPE_TYPE.QMS,
    criticality: CRITICALITY.LOW,
    urgency: URGENCY.NORMAL,
    description: 'Update the controlled procedure for the electronic signature workflow.',
    reasonOrTrigger: 'The signature workflow was simplified.',
    affectedQmsAreas: ['Document control'],
    progress: 100,
    nextAction: 'None',
    plan: buildDemoCompletedPlan(),
    completion: {
      effectivenessReviewRequired: false,
      releaseReadinessCheckedAt: '2026-06-26T09:00:00.000Z',
      releaseReadinessCheckedBy: 'Lukas Poterpin',
      releaseApprovedAt: '2026-06-27T09:00:00.000Z',
      releaseApprovedBy: 'Lukas Poterpin',
      closedAt: '2026-06-27T10:00:00.000Z',
      closedBy: 'Lukas Poterpin',
    },
    auditEvents: [ev('2026-06-27T10:00:00.000Z', 'Lukas Poterpin', AUDIT_ACTION.CHANGE_CLOSED, 'Change closed.')],
  }),

  // 9) Partially completed draft (representative "new draft" / "partially completed").
  createBlankChange('Lukas Poterpin', {
    id: 'chg-draft-01',
    title: 'Revise incoming inspection sampling plan',
    status: CHANGE_STATUS.DRAFT,
    lifecycleStep: LIFECYCLE_STEP.REQUEST,
    createdAt: '2026-07-14T09:00:00.000Z',
    requestDate: '2026-07-14T00:00:00.000Z',
    scopeType: SCOPE_TYPE.QMS,
    urgency: URGENCY.NORMAL,
    description: 'Adjust the incoming inspection sampling plan for a supplier with a strong quality history.',
    reasonOrTrigger: 'Supplier performance data supports a reduced sampling frequency.',
    affectedQmsAreas: ['Supplier management', 'Production and process controls'],
    currentOwner: 'Lukas Poterpin',
    progress: 20,
    nextAction: 'Complete and submit the request',
    auditEvents: [ev('2026-07-14T09:00:00.000Z', 'Lukas Poterpin', AUDIT_ACTION.DRAFT_CREATED, 'Draft created.')],
  }),
];
